'use client';

import React, { memo } from 'react';
import Image from 'next/image';

function Pill({ children, variant = 'neutral' }) {
  const base = 'inline-flex items-center rounded-full px-3 h-8 text-[13px] font-medium border';
  const styles = {
    neutral: 'bg-slate-100 text-slate-700 border-slate-200',
    blue: 'bg-sky-50 text-sky-700 border-sky-200',
    teal: 'bg-teal-50 text-teal-700 border-teal-200',
    gray: 'bg-slate-100 text-slate-700 border-slate-200',
  };
  return <span className={`${base} ${styles[variant]}`}>{children}</span>;
}

function LinkArrow({ children, href = '#' }) {
  return (
    <a
      href={href}
      className="inline-flex items-center gap-2 text-sky-700 font-semibold text-[15px] hover:text-sky-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-700/60 focus-visible:ring-offset-2 rounded-[8px]"
    >
      {children}
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M7 17l7-7M10 7h8v8"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      </svg>
    </a>
  );
}

function WorkInsights() {
  return (
    <section
      aria-labelledby="workinsights-heading"
      className="w-full bg-slate-50 pt-12 sm:pt-14 md:pt-16 lg:pt-20 pb-0"
    >
      <div className="mx-auto w-full max-w-[1240px] px-4 sm:px-6 md:px-8">
        {/* Header */}
        <header className="mb-6 md:mb-8 text-center flex flex-col items-center">
          <h2
            id="workinsights-heading"
            className="text-slate-900 font-extrabold tracking-[-0.01em] text-[clamp(28px,4.8vw,40px)] leading-[1.15]"
          >
            Our Work and Insights
          </h2>
          <p className="mt-2 text-slate-700 text-[clamp(15px,2.2vw,16px)] leading-[clamp(22px,3.1vw,24px)] max-w-[820px]">
            Explore packaging case studies, label finishing techniques, manufacturing updates, sustainability practices and printing innovations from our production floor.
          </p>
        </header>

        {/* Main grid: Left feature + Right news list */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
          {/* Left: Feature story card */}
          <article className="lg:col-span-7">
            <div className="relative overflow-hidden rounded-[16px] bg-slate-200">
              <div className="relative w-full aspect-[16/11] sm:aspect-[4/3]">
                <Image
                  src="/images/portfolio/portfolio-1.jpg"
                  alt="High-quality label production in progress"
                  fill
                  className="object-cover"
                  sizes="(min-width:1024px) 720px, 100vw"
                  priority={false}
                />
              </div>
            </div>

            {/* Tags */}
            <div className="mt-4 flex items-center gap-2">
              <Pill variant="gray">Project</Pill>
              <Pill variant="teal">Industry Tag</Pill>
            </div>

            {/* Title and excerpt */}
            <h3 className="mt-4 text-slate-900 font-semibold text-[20px] leading-[1.3]">
              Sustainable Film Integration
            </h3>
            <p className="mt-2 text-slate-600 text-[15px] leading-[22px] max-w-[70ch]">
              Helped a beverage company transition to recyclable label materials while maintaining shelf appearance, stable adhesion and production line efficiency.
            </p>

          </article>

          {/* Right: Three news cards stacked */}
          <div className="lg:col-span-5">
            <ul className="flex flex-col gap-5 md:gap-6" role="list" aria-label="Latest updates">
              {/* Card item */}
              {[
                {
                  img: '/images/portfolio/cold-foil-technology.jpg',
                  tags: ['News', 'Innovation'],
                  title: 'Cold-Foil Technology',
                  body: 'Discover our latest investment in inline cold-foil systems for unmatched metallic precision on roll form labels.',
                },
                {
                  img: '/images/portfolio/eco_packaging_award.png',
                  tags: ['Sustainability', 'Awards'],
                  title: 'Eco-Packaging Award',
                  body: 'Royal Prime Labels recognized for Excellence in Sustainable Labeling at the National Packaging Awards 2026.',
                },
                {
                  img: '/images/portfolio/portfolio-6.jpg',
                  tags: ['Pharma', 'Compliance'],
                  title: 'Smart Barcoding',
                  body: 'New implementation of high-density QR coding for pharmaceutical traceability and anti-counterfeit measures.',
                },
              ].map((n, i) => (
                <li key={i} className="grid grid-cols-12 gap-4">
                  <div className="col-span-5">
                    <div className="relative overflow-hidden rounded-[12px] bg-slate-200">
                      <div className="relative w-full aspect-[4/3]">
                        <Image
                          src={n.img}
                          alt={n.title}
                          fill
                          className="object-cover"
                          sizes="(min-width:1024px) 220px, 40vw"
                          priority={false}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-span-7">
                    <div className="flex flex-wrap items-center gap-2">
                      <Pill variant="blue">News</Pill>
                      <Pill variant="teal">Industry Tag</Pill>
                    </div>
                    <h4 className="mt-2 text-slate-900 font-semibold text-[16px] leading-[1.35]">
                      {n.title}
                    </h4>
                    <p className="mt-1 text-slate-600 text-[14px] leading-[20px] line-clamp-3">
                      {n.body}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Logo strip */}
        <div className="mt-16 mb-0 py-8 border-t border-b border-[#E4E7EC]">
          <div className="max-w-[1216px] mx-auto px-6 md:px-8">
            <div className="flex flex-wrap justify-center md:justify-between items-center gap-8">
              <img src="/images/iso.png" alt="ISO Certified" className="h-10 grayscale hover:grayscale-0 transition-all opacity-70 hover:opacity-100" />
              <img src="/images/FSC.png" alt="FSC" className="h-10 grayscale hover:grayscale-0 transition-all opacity-70 hover:opacity-100" />
              <img src="/images/GMP.png" alt="GMP" className="h-10 grayscale hover:grayscale-0 transition-all opacity-70 hover:opacity-100" />
              <img src="/images/RoHS.png" alt="RoHS" className="h-10 grayscale hover:grayscale-0 transition-all opacity-70 hover:opacity-100" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default memo(WorkInsights);
